// ability_target_gate_failure.php's status section, shared by player casts, per-effect gates and zone entry.
import { type JanusCtx, type Row, activeStatuses, ecId, ecLean, definitionNotHeld, handBack, itemField, record, scalar, upper } from '../context.js';
import { resolveQuantityAlias, VITALITY_STATUS_IDS } from '../../reactions/trigger-dispatch.js';
import { plCatalogs } from '../../pl/contract.js';
import { resolveReleaseGrappleTargetFromCasterGrappling } from '../../port/k2-geometry/grapple_helpers.js';
import { PhpArray } from '../../php-compat/index.js';
import { layerListEntryUuid } from './status-trigger-gate.js';
import { namedIconName } from '../../compile/possessor-compile.js';
import { escapeHtml } from '../../../shared/cosmetic-card.js';

export const TARGET_ICON = 'target';
const UUID = /^[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}$/;
const RELEASE_GRAPPLE = '3B98A7F7-1A94-4E3F-A85F-25BDC8430874';
const ADVANCE_GRAPPLE = '414E3282-1E29-44B5-95C9-7DA1D55EFCCB';

/** has_status_effect()'s item_field($effect, 'item_id'), including bare-UUID legacy status_arr. */
export const heldStatusItemId = (status: Row): string => upper(status.status_item_id
  ?? (typeof status.status_arr === 'string' ? status.status_arr : itemField(record(status.status_arr), 'item_id')));

/** The vitality reader introduced by animus-v2: a UUID or item shell; null means the tier is unreadable. */
export function targetVStatusId(target: Row): string | null {
  const vitality = record(record(target.computed)?.vitality);
  if (!vitality || !('v_status' in vitality)) return null;
  const raw = vitality.v_status;
  const id = typeof raw === 'string' ? raw : itemField(record(raw), 'item_id') ?? scalar(raw);
  return typeof id === 'string' && id ? upper(id) : null;
}

function gateIds(ctx: JanusCtx, ability: Row, key: string, reason: string): string[] {
  const ids = [...new Set(ctx.read.entries(ability, key).map(layerListEntryUuid))];
  if (!ids.length || ids.some(id => !UUID.test(id))) handBack(`${reason}:invalid-${key === 'if_target_has_status_effect_active' ? 'required' : 'forbidden'}-status`);
  return ids;
}

/** Vitality tiers follow the current cluster; saved tier rows cannot outlive a heal. */
function holds(ctx: JanusCtx, target: Row, id: string): boolean | null {
  const live = ctx.members.get(ecId(target)) ?? target;
  if (id === plCatalogs().statusEffects.getId('dead')
    && Number(ecLean(live, 'encounter_creature_dead') ?? ctx.read.deadState(live)) === 1) return true;
  if (!VITALITY_STATUS_IDS.has(id)) return activeStatuses(ctx, live).some(status => heldStatusItemId(status) === resolveQuantityAlias(id));
  const tier = targetVStatusId(live);
  if (tier === id) return true;
  return tier === null ? null : false;
}

/** get_item_id_name(): NamedIcon / StatusEffect::LIST before the authored item name. */
export function statusDisplayName(ctx: JanusCtx, id: string, reason: string): string {
  const listed = namedIconName(id) ?? plCatalogs().statusEffects.getById(id)?.item_name;
  if (listed) return listed;
  const definition = ctx.read.definition(id);
  if (!definition) definitionNotHeld(`${reason}:status-definition-not-held`, id);
  const name = itemField(definition, 'item_name');
  return typeof name === 'string' && name !== '' && !/[<&>"']/.test(name) ? name : handBack(`${reason}:status-display-name-not-held`);
}

/** OR-list with Release/Advance Grapple's caster pairing before ordinary status rows. */
export function requiredTargetStatusFailure(ctx: JanusCtx, ability: Row, caster: Row, target: Row,
  reason = 'validate_and_adjust_targets'): { ids: string[]; names: string[] } | null {
  if (!ctx.read.has(ability, 'if_target_has_status_effect_active')) return null;
  const ids = gateIds(ctx, ability, 'if_target_has_status_effect_active', reason);
  const verdicts = ids.map(id => holds(ctx, target, id));
  if (verdicts.includes(true)) return null;
  const statuses = plCatalogs().statusEffects;
  const usedId = upper(itemField(ability, 'item_id'));
  if ((usedId === RELEASE_GRAPPLE || usedId === ADVANCE_GRAPPLE) && ids.includes(statuses.getId('grappled')!)) {
    // The existing pure K2 resolver only reads these dependencies; it performs no writes or I/O.
    const env = { deps: {
      creature_statuses: (row: unknown) => {
        const member = record(row) ?? {};
        return activeStatuses(ctx, ctx.members.get(ecId(member)) ?? member);
      },
      item_field: (row: unknown, key: string) => key === 'item_id'
        ? itemField(record(row), key) ?? heldStatusItemId(record(row) ?? {}) : itemField(record(row), key),
      check_uuid: (id: unknown) => UUID.test(upper(id)),
      'StatusEffect::getId': (name: string) => statuses.getId(name),
      encounter_creature_field: (row: unknown, key: string) => ecLean(record(row) ?? {}, key),
    } };
    const pair = resolveReleaseGrappleTargetFromCasterGrappling(caster, { encounter_creatures_arr: [...ctx.members.values()] }, env);
    if (pair instanceof PhpArray && upper(pair.get('grappled_encounter_creature_id')) === ecId(target)) return null;
  }
  if (verdicts.includes(null)) handBack(`${reason}:vitality-tier-not-held`);
  return { ids, names: [...new Set(ids.map(id => statusDisplayName(ctx, id, reason)).filter(Boolean))] };
}

export function requiredTargetStatusMessage(names: string[], postEffect = false, subject = 'The target'): string {
  if (names.length > 1) {
    const list = `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
    return postEffect ? `${subject} currently needs one of these active to be impacted: ${list}!`
      : `${subject} needs one of these active to be affected: ${list}.`;
  }
  const name = names[0] ?? 'the required status effect';
  return postEffect ? `${subject} currently does not have the status effect ${name} active to impact the target!`
    : `${subject} does not have the status effect ${name} active to be affected.`;
}

/** PHP stops at the first active forbidden status; the failure log still names the entire authored list. */
export function forbiddenTargetStatusFailure(ctx: JanusCtx, ability: Row, target: Row, reason: string): { id: string; ids: string[] } | null {
  if (!ctx.read.has(ability, 'if_target_has_status_effect_not_active')) return null;
  const ids = gateIds(ctx, ability, 'if_target_has_status_effect_not_active', reason);
  for (const id of ids) {
    const held = holds(ctx, target, id);
    if (held === null) handBack(`${reason}:vitality-tier-not-held`);
    if (held) return { id, ids };
  }
  return null;
}

/** Both target-status gates as a PREDICATE (no refusal text, so no display-name lookup): any required id held, no forbidden id held. A roller's
 *  gated hit_to_crit carrier (skill-roll.ts rollerHitToCrit — Melting Judgement) asks it; an unmet gate grants nothing rather than refusing. */
export function targetStatusGatesMet(ctx: JanusCtx, ability: Row, target: Row, reason: string): boolean {
  if (ctx.read.has(ability, 'if_target_has_status_effect_active')) {
    const verdicts = gateIds(ctx, ability, 'if_target_has_status_effect_active', reason).map(id => holds(ctx, target, id));
    if (!verdicts.includes(true)) {
      if (verdicts.includes(null)) handBack(`${reason}:vitality-tier-not-held`);
      return false;
    }
  }
  return forbiddenTargetStatusFailure(ctx, ability, target, reason) === null;
}

export function forbiddenTargetStatusMessage(name: string, postEffect = false, subject = 'The target'): string {
  return postEffect ? `${subject} currently has the status effect ${name} active to impact the target!`
    : `${subject} has the status effect ${name} active and cannot be affected.`;
}

/** Required-then-forbidden ordering; zone entry remains silent. Refusals name the target in plain text. */
export function targetStatusGateFailure(ctx: JanusCtx, ability: Row, target: Row, reason: string,
  { forbidden = true, caster = {} }: { forbidden?: boolean; caster?: Row } = {}): (() => string) | null {
  const subject = escapeHtml(String(ecLean(target, 'encounter_creature_name') ?? itemField(target, 'item_name') ?? 'The target'));
  const required = requiredTargetStatusFailure(ctx, ability, caster, target, reason);
  if (required) return () => requiredTargetStatusMessage(required.names, false, subject);
  const blocked = forbidden ? forbiddenTargetStatusFailure(ctx, ability, target, reason) : null;
  if (blocked) return () => forbiddenTargetStatusMessage(statusDisplayName(ctx, blocked.id, reason), false, subject);
  return null;
}
